import React from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import styled, { theme, rem } from '@nobia/zeus-components/lib/styled'
import { Icon } from '@nobia/zeus-components/lib/icons'
import { injectIntl } from '@nobia/zeus-components/lib/i18n'
import { compose, withHandlers } from '@nobia/zeus-components/lib/recompose'
import {
  cloudinaryUrlTransformation,
  galleryListTransformation,
} from '../utils'
import LazyLoad from '../../../components/lazy-load/LazyLoad'

const ImageLabelText = styled.span`
  color: ${theme('typography.imageLabelText.color')};
  font-size: ${theme('typography.imageLabelText.fontSize')};
  font-weight: ${theme('typography.imageLabelText.fontWeight')};
  text-transform: ${theme('typography.imageLabelText.textTransform')};
  letter-spacing: ${theme('typography.imageLabelText.letterSpacing')};
`
const ImageLabelCount = styled.span`
  margin-left: 10px;
`

const ImageLabelMask = styled.div`
  position: absolute;
  width: 100%;
  height: 47px;
  background-color: #ffffff;
  left: 0px;
  bottom: -47px;
  color: #999;
  opacity: 1;
  display: block;
  z-index: 2;
`

const ImageLabelAndTitleWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  right: 0px;
  bottom: 0px;
  opacity: 0;
`

const CollectionLabel = styled.div`
  position: absolute;
  left: 0px;
  bottom: ${theme('collectionLabel.bottom')};
  width: 100%;
  padding-left: 30px;
  font-size: ${theme('typography.collectionLabel.fontSize')};
  text-decoration: none;
  color: #fff;
  text-transform: ${theme('typography.collectionLabel.textTransform')};
  font-weight: ${theme('typography.collectionLabel.fontWeight')};
  letter-spacing: ${theme('typography.collectionLabel.letterSpacing')};
`

const ImageLabel = styled.div`
  position: absolute;
  height: ${theme('image.ImageLabel.height')};
  border-radius: ${theme('image.ImageLabel.borderRadius')};
  padding: ${theme('image.ImageLabel.padding')};
  background-color: ${theme('image.ImageLabel.backgroundColor')};
  border: ${theme('image.ImageLabel.border')};
  left: 30px;
  bottom: 20px;
  cursor: pointer;
  color: #999;
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledImgWrapper = styled.div`
  width: 100%;
  margin-bottom: ${rem(48)};
  position: relative;
  font-size: 14px;
`

const StyledIcon = styled(Icon)`
  ${'' /* display: flex; */}
  margin-left: 2px;
  /* opacity: 0.42; */
  color: #383838;
  display: ${theme('image.StyledIcon.display')};
  svg {
    width: 13px;
    height: 11px;
  }
`

const StyledFigure = styled.div`
  width: 100%;
  cursor: pointer;
  position: relative;
  display: flex;

  & {
    ${ImageLabelAndTitleWrapper} {
      -webkit-animation: fallOut 0.3s 0ms 1;
      -moz-animation: fallOut 0.3s 0ms 1;
      -o-animation: fallOut 0.3s 0ms 1;
      animation: fallOut 0.3s 0ms 1;
      animation: fallOut 0.3s 0ms 1;

      @keyframes fallOut {
        0% {
          bottom: 10px;
          opacity: 1;
        }
        99% {
          bottom: -60px;
          opacity: 1;
        }
        100% {
          bottom: -70px;
          opacity: 0;
        }
      }
    }
  }
  picture {
    width: ${theme('image.thumbnail.width')};
  }
  img {
    transition: ${theme('image.thumbnail.transition')};
    filter: ${theme('image.thumbnail.filter')};
  }
  &:hover {
    img {
      filter: ${theme('image.thumbnail.hover.filter')};
    }
    ${ImageLabelAndTitleWrapper} {
      -webkit-animation: moveUp 0.3s 0ms 1 forwards;
      -moz-animation: moveUp 0.3s 0ms 1 forwards;
      -o-animation: moveUp 0.3s 0ms 1 forwards;
      animation: moveUp 0.3s 0ms 1 forwards;
      animation: moveUp 0.3s 0ms 1 forwards;

      @keyframes moveUp {
        0% {
          bottom: -80px;
          opacity: 0;
        }
        10% {
          bottom: -70px;
          opacity: 0;
        }
        20% {
          bottom: -60px;
          opacity: 1;
        }
        100% {
          bottom: 0px;
          opacity: 1;
        }
      }
    }
  }
`

const GallerPlaceholder = styled.div`
  width: 100%;
  height: 0px;
  padding-bottom: ${props => (props.portrait ? '138.6%' : '64.4%')};
  margin-bottom: 3rem;
`

const GallerPlaceholderComponent = image => (
  <GallerPlaceholder portrait={image.width < image.height} />
)

const ImageGallery = ({
  imageCollections,
  className,
  openModalFunc,
  getString,
  location,
  themes,
}) => {
  const parsedHash = queryString.parse(location.hash)
  const breakpoint =
    themes && themes.breakpoints && themes.breakpoints.sm
      ? themes.breakpoints.sm + 0
      : 0
  if (
    location &&
    location.hash &&
    parsedHash.gid &&
    parsedHash.pid &&
    document &&
    document.documentElement &&
    document.documentElement.clientWidth &&
    breakpoint <= document.documentElement.clientWidth
  ) {
    if (imageCollections && imageCollections.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < imageCollections.length; i++) {
        const dataArray = imageCollections[i]
        const imageCount =
          (dataArray &&
            dataArray.images &&
            dataArray.images.expandedValue &&
            dataArray.images.expandedValue.length) ||
          0
        if (parsedHash.gid === dataArray.contentLink.id && imageCount > 0) {
          setTimeout(() => {
            openModalFunc(imageCount, dataArray.contentLink.id, true)
          }, 1000)
        }
      }
    }
  }

  return (
    <div className="pswp-gallery">
      <div
        className="pswp-thumbnails"
        itemScope="itemScope"
        itemType="http://schema.org/ImageGallery"
      >
        {imageCollections &&
          imageCollections.length > 0 &&
          imageCollections.map(dataArray => (
            <div
              key={dataArray.contentLink.id}
              data-pswp-uid={dataArray.contentLink.id}
              className={className}
            >
              {dataArray &&
                dataArray.images.expandedValue &&
                dataArray.images.expandedValue.length > 0 && (
                  <LazyLoad
                    threshold={10}
                    placeholder={GallerPlaceholderComponent(
                      JSON.parse(dataArray.images.expandedValue[0].image.value)
                    )}
                  >
                    <StyledImgWrapper
                      key={dataArray.contentLink.id}
                      onClick={() =>
                        openModalFunc(
                          dataArray.images.expandedValue.length,
                          dataArray.contentLink.id
                        )
                      }
                      id={dataArray.contentLink.id}
                      data-count={dataArray.images.expandedValue.length}
                      itemProp="associatedMedia"
                      itemScope="itemScope"
                      itemType="http://schema.org/ImageObject"
                    >
                      <StyledFigure>
                        {JSON.parse(
                          dataArray.images.expandedValue[0].image.value
                        ).resourceType &&
                        JSON.parse(
                          dataArray.images.expandedValue[0].image.value
                        ).resourceType === 'video' ? (
                          <picture>
                            <source
                              media="only screen and (-webkit-min-device-pixel-ratio: 1.25),
                                only screen and ( min--moz-device-pixel-ratio: 1.25),
                                only screen and ( -o-min-device-pixel-ratio: 1.25/1),
                                only screen and ( min-device-pixel-ratio: 1.25),
                                only screen and ( min-resolution: 200dpi),
                                only screen and ( min-resolution: 1.25dppx)"
                              srcSet={`${cloudinaryUrlTransformation(
                                dataArray.images.expandedValue[0].image.value,
                                galleryListTransformation(
                                  dataArray.images.expandedValue[0].image.value,
                                  'w_1010/w_144,l_play'
                                ),
                                ''
                              )}.jpg`}
                            />

                            <img
                              src={`${cloudinaryUrlTransformation(
                                dataArray.images.expandedValue[0].image.value,
                                galleryListTransformation(
                                  dataArray.images.expandedValue[0].image.value,
                                  'w_505/w_72,l_play'
                                ),
                                ''
                              )}.jpg`}
                              loading="lazy"
                              itemProp="thumbnail"
                              alt={
                                dataArray.images.expandedValue[0].title.value
                              }
                            />
                          </picture>
                        ) : (
                          <picture>
                            <source
                              media="only screen and (-webkit-min-device-pixel-ratio: 1.25),
                                only screen and ( min--moz-device-pixel-ratio: 1.25),
                                only screen and ( -o-min-device-pixel-ratio: 1.25/1),
                                only screen and ( min-device-pixel-ratio: 1.25),
                                only screen and ( min-resolution: 200dpi),
                                only screen and ( min-resolution: 1.25dppx)"
                              srcSet={cloudinaryUrlTransformation(
                                dataArray.images.expandedValue[0].image.value,
                                galleryListTransformation(
                                  dataArray.images.expandedValue[0].image.value,
                                  'w_1010'
                                ),
                                dataArray.images.expandedValue[0]
                                  .friendlyFileName.value
                              )}
                            />

                            <img
                              src={cloudinaryUrlTransformation(
                                dataArray.images.expandedValue[0].image.value,
                                galleryListTransformation(
                                  dataArray.images.expandedValue[0].image.value,
                                  'w_756'
                                ),
                                dataArray.images.expandedValue[0]
                                  .friendlyFileName.value
                              )}
                              loading="lazy"
                              itemProp="thumbnail"
                              alt={
                                dataArray.images.expandedValue[0].title.value
                              }
                            />
                          </picture>
                        )}
                        <ImageLabelAndTitleWrapper>
                          <CollectionLabel>
                            {dataArray.title.value}
                          </CollectionLabel>
                          <ImageLabel>
                            <ImageLabelText>
                              {getString(
                                'viewcollection',
                                'View collection',
                                dataArray.viewCollectionLabel
                                  ? dataArray.viewCollectionLabel.value
                                  : ''
                              )}
                              <ImageLabelCount>
                                {dataArray.images.expandedValue.length}
                              </ImageLabelCount>
                              <StyledIcon type="photo" />
                            </ImageLabelText>
                          </ImageLabel>
                        </ImageLabelAndTitleWrapper>
                      </StyledFigure>
                      <ImageLabelMask />
                    </StyledImgWrapper>
                  </LazyLoad>
                )}
            </div>
          ))}
      </div>
    </div>
  )
}

ImageGallery.propTypes = {
  imageCollections: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  className: PropTypes.string.isRequired,
  openModalFunc: PropTypes.func.isRequired,
  getString: PropTypes.func.isRequired,
  location: PropTypes.shape().isRequired,
  themes: PropTypes.shape().isRequired,
}

export default compose(
  injectIntl,
  withHandlers({
    getString: ({ intl, language }) => (key, defaultMessage, text) => {
      if (text) return text
      return intl.formatMessage({
        id: `app.${language}.${key}`,
        defaultMessage,
      })
    },
  })
)(ImageGallery)
